import { useQuery } from '@tanstack/react-query'
import { client } from '../client'
import { queryKeys } from '../keys'
import type { components } from '@/lib/generated/api-types'

// Type exports for convenience
export type SearchStatus = components['schemas']['SearchStatus']
export type SearchParams = components['schemas']['SearchParams']
export type SearchResult = components['schemas']['SearchResult']
export type SearchFilterResponse = components['schemas']['SearchFilterResponse']
export type SearchFiltersResponse =
  components['schemas']['SearchFiltersResponse']
export type LoopIntervalsResponse =
  components['schemas']['LoopIntervalsResponse']
export type LoopIntervalsRequest = components['schemas']['LoopIntervalsRequest']

// Get available search filters
export function useSearchFilters() {
  return useQuery({
    queryKey: queryKeys.searches.filters(),
    queryFn: async () => {
      const { data } = await client.GET('/api/v1/search/filters')
      return data
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour since filters don't change often
  })
}

// Get current loop intervals
export function useLoopIntervals() {
  return useQuery({
    queryKey: queryKeys.searches.loopIntervals(),
    queryFn: async () => {
      const { data, error } = await client.GET('/api/v1/search/loop-intervals')
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (error) throw error
      return data
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })
}

// Get search status with automatic polling while search is running
export function useSearchStatus(
  accountName: string | undefined,
  enabled = false,
) {
  return useQuery({
    queryKey: queryKeys.searches.current(accountName!),
    queryFn: async () => {
      const { data, error } = await client.GET(
        '/api/v1/accounts/{account_name}/searches/current',
        {
          params: { path: { account_name: accountName! } },
        },
      )
      if (error) throw error
      return data
    },
    enabled: !!accountName && enabled,
    staleTime: 0, // Always refetch when polling
    refetchInterval: (query) => {
      // Poll every second while search is running
      const status = query.state.data?.status
      if (status === 'running') {
        return 1000 // 1 second
      }
      return false // Stop polling when not running
    },
  })
}
